import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { createAction, props } from '@ngrx/store';
import { Map, Set } from 'immutable';

import { AppMetadataEntity } from '../../ui/components/app-metadata/app-metadata.entity';
import { ExportCharsetEntity } from '../components/export-template-details/export-charset.entity';
import { ExportConnectorEntity } from '../components/export-template-details/export-connector.entity';
import { ExportTemplateDetailsEntity } from '../components/export-template-details/export-template-details.entity';
import { ExportTemplateListEntity } from '../components/export-template-list/export-template-list.entity';

export enum ExportTemplateActions {
	InitExportConfig = '[Export] init export',
	FetchExportConfig = '[Export] Get template config',
	DeleteById = '[Export] delete by Id',
	DeleteByIdSuccess = '[BE][Export] Successfully deleted export template',
	GoToExportTemplateListView = '[Export] List view',
	FetchExportConfigSuccess = '[BE][Export] Get template config success',
	FetchExportConfigFailure = '[BE][Export] Get template config Failure',
	FetchAvailableConnectorsSuccess = '[BE][Export] Successfully retrieved available connectors for current project',
	CreateExportConfig = '[BE][Export] save export template',
	UpdateExportConfig = '[BE][Export] update export template',
	CreateExportConfigSuccess = '[BE][Export] save export template success',
	CreateExportConfigFail = '[BE][Export] save  export template failed',
	FetchExportTemplateList = '[Export] List of export template',
	FetchExportTemplateListSuccess = '[BE][Export] Get list of export template success',
	FetchExportTemplateListFail = '[BE][Export] Get list of export template failure',
	ConfigureExportDatablockById = '[Export] Open export configuration modal for selected datablock',
	ResetExportTemplateSelection = '[Export] reset pre-configured export template selection',
	ExportExecutionFinished = '[Export] Export execution finished',
	InitExportDatablockUsingExistingTemplate = '[Datablock List] Init Export currently selected Datablock using existing template',
	SearchAvailableExportTemplate = '[Datablock List] Search available export template',
	FetchSelectedExportTemplateConfig = '[Datablock List][Export] Fetch selected export template config',
	FetchPreConfiguredExportTemplates = '[Datablock List][Export] Fetch all preconfigured export templates',
	ExecuteExportUsingTemplate = '[Datablock List][Export] Execute export using selected/configured template',
	CloseExecuteExportUsingTemplateModal = '[Datablock List][Export] Close execute export using template modal',
	GetAllAvailableTemplates = '[Datablock List][Export] Get all available templates',
	InitExportTemplateList = '[Export] get export template list',
	RefreshExportTemplateList = '[Export] refresh export template list',
	NavigateToExportTemplateEditRoute = '[Export] navigate to export template edit route',
	NavigateToExportTemplateCreateRoute = '[Export] navigate to export template create route',
	ToggleExportTemplateStatus = '[Export] toggle export template status',
}

export const initExportTemplateConfig = createAction(
	ExportTemplateActions.InitExportConfig
);

export const goToExportTemplateListView = createAction(
	ExportTemplateActions.GoToExportTemplateListView
);

export const resetExportTemplate = createAction(
	ExportTemplateActions.ResetExportTemplateSelection
);

export const exportExecutionFinished = createAction(
	ExportTemplateActions.ExportExecutionFinished
);

export const fetchExportTemplateList = createAction(
	ExportTemplateActions.FetchExportTemplateList,
	props<{
		excludeInactive: boolean;
	}>()
);

export const deleteExportTemplatesById = createAction(
	ExportTemplateActions.DeleteById,
	props<{
		elemIds: Array<number>;
		excludeInactive: boolean;
	}>()
);

export const deleteExportTemplatesByIdSuccess = createAction(
	ExportTemplateActions.DeleteByIdSuccess,
	props<{
		excludeInactive: boolean;
	}>()
);

export const fetchExportTemplateListSuccess = createAction(
	ExportTemplateActions.FetchExportTemplateListSuccess,
	props<{
		list: ExportTemplateListEntity;
	}>()
);

export const fetchExportTemplateListFailure = createAction(
	ExportTemplateActions.FetchExportTemplateListFail,
	props<{ error: unknown }>()
);

export const fetchExportTemplateConfig = createAction(
	ExportTemplateActions.FetchExportConfig,
	props<{
		exportId: number;
	}>()
);

export const fetchExportConfigSuccess = createAction(
	ExportTemplateActions.FetchExportConfigSuccess,
	props<{ entity: ExportTemplateDetailsEntity; metadata: AppMetadataEntity }>()
);

export const fetchExportConfigFailure = createAction(
	ExportTemplateActions.FetchExportConfigFailure,
	props<{ error: unknown }>()
);

export const initExportDatablockUsingExistingTemplate = createAction(
	ExportTemplateActions.InitExportDatablockUsingExistingTemplate,
	props<{
		excludeInactive: boolean;
	}>()
);

export const searchAvailableExportTemplate = createAction(
	ExportTemplateActions.SearchAvailableExportTemplate,
	props<{
		searchTerm: string;
	}>()
);

export const fetchSelectedExportTemplateConfig = createAction(
	ExportTemplateActions.FetchSelectedExportTemplateConfig,
	props<{
		exportId: number;
	}>()
);

export const fetchAvailableConnectorsSuccess = createAction(
	ExportTemplateActions.FetchAvailableConnectorsSuccess,
	props<{ connectors: Map<number, ExportConnectorEntity> }>()
);

export const fetchAvailableCharsetsSuccess = createAction(
	'[BE][Charsets] Successfully retrieved available charsets',
	props<{ charsets: Set<ExportCharsetEntity> }>()
);

export const fetchAvailableCharsetsFailure = createAction(
	'[BE][Charsets] Failure while retrieving charsets',
	props<{ error: unknown }>()
);

export const createExportTemplateConfig = createAction(
	ExportTemplateActions.CreateExportConfig,
	props<{
		payload: {
			templateDetails: ExportTemplateDetailsEntity;
			templateMetadata: AppMetadataEntity;
			templateTags: Array<AppTagItemEntity>;
		};
	}>()
);

export const updateExportTemplateConfig = createAction(
	ExportTemplateActions.UpdateExportConfig,
	props<{
		payload: {
			templateDetails: ExportTemplateDetailsEntity;
			templateMetadata: AppMetadataEntity;
			templateTags: Array<AppTagItemEntity>;
		};
	}>()
);

export const createExportTemplateConfigSuccess = createAction(
	ExportTemplateActions.CreateExportConfigSuccess,
	props<{
		templateId: number;
		exportType: string;
		isNew: boolean;
	}>()
);

export const createExportTemplateConfigFailure = createAction(
	ExportTemplateActions.CreateExportConfigFail,
	props<{ error: unknown }>()
);

export const configureSelectedDatablockExport = createAction(
	ExportTemplateActions.ConfigureExportDatablockById,
	props<{
		datablockId: number;
		datablockLabel: string;
	}>()
);

export const fetchPreConfiguredExportTemplates = createAction(
	ExportTemplateActions.FetchPreConfiguredExportTemplates
);

export const executeExportUsingTemplate = createAction(
	ExportTemplateActions.ExecuteExportUsingTemplate,
	props<{
		exportTemplate: ExportTemplateDetailsEntity;
	}>()
);

export const closeExecuteExportUsingTemplateModal = createAction(
	ExportTemplateActions.CloseExecuteExportUsingTemplateModal
);

export const getAllAvailableTemplates = createAction(
	ExportTemplateActions.GetAllAvailableTemplates
);

export const initExportTemplateList = createAction(
	ExportTemplateActions.InitExportTemplateList
);

export const refreshExportTemplateList = createAction(
	ExportTemplateActions.RefreshExportTemplateList
);

export const navigateToExportTemplateCreate = createAction(
	ExportTemplateActions.NavigateToExportTemplateCreateRoute
);

export const navigateToExportTemplateEdit = createAction(
	ExportTemplateActions.NavigateToExportTemplateEditRoute,
	props<{ exportConfigId: number }>()
);

export const toggleExportStatus = createAction(
	ExportTemplateActions.ToggleExportTemplateStatus,
	props<{ exportId: number }>()
);

export const predictNormalizedDatablockLabel = createAction(
	'[Export] Predict Normalized Datablock Label',
	props<{ datablockId: number; connectorId: number }>()
);

export const predictNormalizedDatablockLabelSuccess = createAction(
	'[BE][Export] Predict Normalized Datablock Label Success',
	props<{ normalizedLabel: string }>()
);

export const predictNormalizedDatablockLabelFailure = createAction(
	'[BE][Export] Predict Normalized Datablock Label Failure',
	props<{ error: unknown }>()
);
